import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux';
import { Typography, Grid, Box, Chip, Snackbar, Tooltip, Dialog } from '@mui/material';
import { Alert } from "@mui/lab";
import { Translate, withLocalize } from 'react-localize-redux';
import Helmet from "react-helmet";
import { decryptData } from '../../../utils/index.jsx';
import { isArgon2Auth } from '../../../utils/crypto';
import Loader from '../../Loader';
import { BoxBckgr, ButtonAdd, ButtonContinue, TypographyStyled } from '../../general/mini_components';
import Modal from '../../general/modal';
import Form from '../../general/form';
import { EnhancedTable } from "../../general/EnhancedTable";
import { yellow, green, blue, red, orange, purple, grey } from "@mui/material/colors";
import { useHistory } from "react-router-dom";
import { deleteResearcher, getSharedResearchersService } from '../../../services';
import ResetPasswordFlow from './ResetPasswordFlow';
import { getRolesForInvestigationService, assignRoleBatchService } from '../../../services/roles';
import SectionHeader from '../../../pages/components/SectionHeader';
import UserRoles from './UserRoles';
import RolesManagement from './RolesManagement';
import { useSnackBarState } from "../../../hooks"
import { ALL_ROLES, PERMISSION } from './user_roles';
import { ColourChip } from '../../general/mini_components-ts';
import SearchBox from '../../general/SearchBox';
import { TabsSherwood } from '../../../pages/components/Tabs';
import { ResearcherVersionCell } from './ResearcherVersionCell';
import InvitationLinks from '../../invitation/InvitationLinks';
import { readStoredKeks, resendInvitationWithToken, shareInvestigationWithTokens } from '../../../services/invitations';



const ROLE_COLOURS = {
    // SkincAIr roles — reuse the existing palette so chips stay consistent with
    // legacy roles in mixed tenants. Mapping rationale:
    //   SUPER_ADMIN     -> orange[900]  (was MEDICAL_DIRECTOR, displayed as "Superadmin")
    //   DERMATOLOGIST   -> orange[700]  (same shade as DOCTOR — clinical role)
    //   DATA_REVIEWER   -> blue[500]    (like ADMIN — read-oriented dashboard role)
    //   FIELD_COORDINATOR -> blue[300]  (like BUSINESS_ASSISTANT — supporting role)
    "SUPER_ADMIN": orange[900],
    "DERMATOLOGIST": orange[700],
    "DATA_REVIEWER": blue[500],
    "FIELD_COORDINATOR": blue[300],
    "MEDICAL_DIRECTOR": orange[900],
    "DOCTOR": orange[700],
    "SOCIAL": orange[700],
    "PRIVATE_DOCTOR": orange[100],
    "DEPARTMENT_HEAD": orange[100],
    "STUDENT": orange[500],
    "NURSE": orange[100],
    "NURSE_FW": orange[100],
    "NURSE_MANAGER": yellow[900],
    "CARE_GIVER": blue[900],
    "BUSINESS_MANAGER": blue[900],
    "ADMIN": blue[500],
    "ADMIN_FW": blue[400],
    "CASHIER": blue[400],
    "CASHIER_MANAGER": blue[700],
    "BUSINESS_ASSISTANT": blue[300],
    "LAB_MANAGER": yellow[900],
    "LAB_ASSISTANT": yellow[500],
    "SHERWOOD_STAFF": green[500],
    "PHARMACY_MANAGER": red[900],
    "PHARMACY_ASSITANT": red[700],
    "MAJOR": red[500],
    "EXECUTIVE_DIRECTOR": purple[500],
    "SURGEON": purple[500],
};

export const PermissionChip = withLocalize((props) => {
    const role = props.role; // { id, name } from API
    const permissions = props.value || [];
    const roles = props.availableRoles || [];

    // Prefer role from API (shared researchers list returns role only)
    if (role && role.name) {
        const colour = ROLE_COLOURS[role.name] || blue[500];
        const translatedName = props.translate("investigation.share.roles." + role.name);
        const label = translatedName.indexOf("Missing translationId:") !== -1 ? role.name : translatedName;
        const apiRole = roles.find(r => r.name === role.name || r.id === role.id);
        const tooltipPermissions = apiRole && apiRole.permissions && apiRole.permissions.length > 0
            ? apiRole.permissions
            : [];
        const chip = <ColourChip rgbcolor={colour} label={label} />;
        if (tooltipPermissions.length > 0) {
            const tooltipContent = (
                <Box>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {tooltipPermissions.map((perm, index) => (
                            <li key={index}>
                                <Translate id={`investigation.share.permissions.${perm}`} />
                            </li>
                        ))}
                    </ul>
                </Box>
            );
            return (
                <Tooltip title={tooltipContent} arrow placement="top">
                    <span>{chip}</span>
                </Tooltip>
            );
        }
        return chip;
    }

    // Fallback: match permissions array to availableRoles (e.g. batch-assign response)
    const matchedApiRole = roles.find(r =>
        r.permissions.length === permissions.length &&
        r.permissions.every(p => permissions.includes(p))
    );

    if (matchedApiRole) {
        const colour = ROLE_COLOURS[matchedApiRole.name] || blue[500];
        const translatedName = props.translate("investigation.share.roles." + matchedApiRole.name);
        const label = translatedName.indexOf("Missing translationId:") !== -1 ? matchedApiRole.name : translatedName;
        const chip = <ColourChip rgbcolor={colour} label={label} />;
        if (matchedApiRole.permissions && matchedApiRole.permissions.length > 0) {
            const tooltipContent = (
                <Box>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {matchedApiRole.permissions.map((perm, index) => (
                            <li key={index}>
                                <Translate id={`investigation.share.permissions.${perm}`} />
                            </li>
                        ))}
                    </ul>
                </Box>
            );
            return (
                <Tooltip title={tooltipContent} arrow placement="top">
                    <span>{chip}</span>
                </Tooltip>
            );
        }
        return chip;
    }

    // Fallback: match against hardcoded ALL_ROLES
    const roleFromPerms = permissionsToRole(permissions);

    if (roleFromPerms && roleFromPerms !== "NO_PERMISSIONS" && ROLE_COLOURS[roleFromPerms]) {
        return <ColourChip rgbcolor={ROLE_COLOURS[roleFromPerms]} label={props.translate("investigation.share.roles." + roleFromPerms)} />;
    }

    // No role matched
    const noRoleChip = <ColourChip rgbcolor={grey[500]} label={props.translate("investigation.share.roles.NO_ROLE_ASSIGNED")} />;
    if (permissions.length > 0) {
        const tooltipContent = (
            <Box>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {permissions.map((perm, index) => (
                        <li key={index}>
                            <Translate id={`investigation.share.permissions.${perm}`} />
                        </li>
                    ))}
                </ul>
            </Box>
        );
        return (
            <Tooltip title={tooltipContent} arrow placement="top">
                <span>{noRoleChip}</span>
            </Tooltip>
        );
    }
    return noRoleChip;
})

const StatusChip = withLocalize((props) => {
    switch(props.value.toString()){
        case "0":
            return <ColourChip size="small" rgbcolor={yellow[800]} label={props.translate("investigation.share.status.pending")} />
        case "1": 
            return <ColourChip size="small"rgbcolor={red[500]} label={props.translate("investigation.share.status.denied")} />
        default:
            return <ColourChip size="small" rgbcolor={green[500]} label={props.translate("investigation.share.status.accepted")} />
    }
    
})

function permissionsToRole(permissions){
    if(permissions.length === 0){
        return "NO_PERMISSIONS";
    }
    let roleFound = false;
    const keyRolesArray = Object.keys(ALL_ROLES);
    let index = 0;
    while(!roleFound && index < keyRolesArray.length){
        const keyRole = keyRolesArray[index];
        const rolePermissions = ALL_ROLES[keyRole];
        const containsAll = rolePermissions.every(arr2Item => permissions.includes(arr2Item)) && (rolePermissions.length === permissions.length)
        
        if(containsAll){
            return keyRole;
        }
        else{
            index++;
        }
    }
    
}

function ShareInvestigation(props) {
    
    const investigation = props.investigations.data && props.investigations.currentInvestigation ? props.investigations.currentInvestigation : null;
    const [error, setError] = useState(null);
    const [ addingResearcher, setAddingResearcher ] = useState(false);
    const [ newResearchers, setNewResearchers ] = useState(props.initialState && props.initialState.newResearchers ? props.initialState.newResearchers : []);
    const [ showModal, setShowModal] = useState(false);
    const [ sharedResearchers, setSharedResearchers] = useState([]);
    const [isLoadingShare, setIsLoadingShare] = useState(false);
    const [errorShare, setErrorShare] = useState(false);
    const [indexResearcherToEdit, setIndexResearcherToEdit] = useState(false);
    const [indexResearcherToDelete, setIndexResearcherToDelete] = useState(false);
    const [researcherToDelete, setResearcherToDelete] = useState([]);
    const [researcherToResend, setResearcherToResend] = useState(null);
    // SHER-803 — the accept links of the invitations just sent or re-sent. They exist only here:
    // the API never holds the secret they carry, so it cannot email them.
    const [invitationLinks, setInvitationLinks] = useState(null);
    const resendInFlight = useRef(false);
    const [researcherToReset, setResearcherToReset] = useState(null);
    const [showingRoles, setShowingRoles] = useState(false);
    const [showSnackbar, setShowSnackbar] = useSnackBarState();
    const [searchText, setSearchText] = useState("");
    const [filteredResearchers, setFilteredResearchers] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const history = useHistory();

    const optionsRoles = availableRoles.map(role => {
        const translatedName = props.translate("investigation.share.roles." + role.name);
        const label = translatedName.indexOf("Missing translationId:") !== -1 ? role.name : "investigation.share.roles." + role.name;
        return { "label": label, "value": role.id.toString() };
    });

    const RESEARCHER_FORM = {
        "email": {
            required: true,
            type: "text",
            name: "email",
            label: "investigation.share.form.email",
            shortLabel: "investigation.share.form.email",
            validation: "validEmail"
        },
        "roleId": {
            required: true,
            type: "select",
            name: "roleId",
            label: "investigation.share.form.role",
            shortLabel: "investigation.share.form.role",
            validation: "notEmpty",
            defaultOption: { "text": "investigation.create.edc.choose", "value": "0" },
            options: optionsRoles
        },
    };
    
    function shareInvestigation(){
        setShowModal(true)
    }
    function resetModal(){
        console.log("Close modal2");
        setShowModal(false);
        setIndexResearcherToEdit(false);
        setAddingResearcher(false);
        setShowingRoles(false);
        setIndexResearcherToDelete(false)
        setResearcherToResend(null);
        setResearcherToReset(null);
    }
    /**
     * What this inviter can hand over: the v1 investigation key (on this line "" for every argon2
     * investigation) and, under argon2, the three KEKs sign-in stored for this investigation.
     * SHER-344: with neither, the session has lost its keys and inviting would hand every invitee
     * nothing, so refuse.
     */
    function resolveInviterKeys(){
        const rawKeyInvestigation = decryptData(investigation.keyResearcherInvestigation, localStorage.getItem("rawKeyResearcher"));
        const keks = isArgon2Auth() ? readStoredKeks(investigation.uuid) : {};
        if(!rawKeyInvestigation && Object.keys(keks).length === 0){
            setShowSnackbar({show:true, message:"investigation.share.error.title", severity:"error"});
            return null;
        }
        return { rawKeyInvestigation, keks };
    }

    async function sendInvitations(){
        setShowModal(false);
        setIsLoadingShare(true);
        try{
            const keys = resolveInviterKeys();
            if(!keys){
                setIsLoadingShare(false);
                return;
            }
            // SHER-803: each invitee gets their own token; the v1 key and the KEKs travel wrapped
            // under it (share/v2 when there are KEKs), and the links come back here to be handed
            // over by the inviter. The public constant is no longer used to wrap anything.
            const result = await shareInvestigationWithTokens(
                props.investigations.currentInvestigation.uuid,
                newResearchers.map(researcher => ({ email: researcher.email, roleId: parseInt(researcher.roleId) })),
                keys.rawKeyInvestigation,
                window.location.origin,
                keys.keks
            );

            if(result.status === 200){
                setSharedResearchers(result.sharedResearchers);
                setNewResearchers([]);
                setInvitationLinks(result.links);
                setShowSnackbar(result.missingLinks && result.missingLinks.length > 0
                    ? {show:true, message:"invitation.links.missing", severity:"warning"}
                    : {show:true, message:"investigation.share.success", severity:"success"});
            }
            else{
                setShowSnackbar({show:true, message:"investigation.share.error.already_invited", severity:"error"});
            }
        }
        catch(error){
            console.log(error);
            setErrorShare(true);
        }
        setIsLoadingShare(false);
    }

    function copyInvitationLink(link){
        navigator.clipboard.writeText(link)
            .then(() => setShowSnackbar({show:true, message:"invitation.links.copied", severity:"success"}))
            .catch(() => setShowSnackbar({show:true, message:"investigation.share.error.title", severity:"error"}));
    }

    async function refetchRoles(uuidInvestigation) {
        try {
            const rolesResponse = await getRolesForInvestigationService(uuidInvestigation);
            setAvailableRoles([...rolesResponse.result.defaultRoles, ...rolesResponse.result.customRoles]);
        } catch (err) {
            console.log("Error fetching roles:", err);
        }
    }

    useEffect(()=>{
        async function fetchData(uuidInvestigation){
            setIsLoadingShare(true);
            try {
                const [researchersResponse, rolesResponse] = await Promise.all([
                    getSharedResearchersService(uuidInvestigation),
                    getRolesForInvestigationService(uuidInvestigation)
                ]);
                setSharedResearchers(researchersResponse.sharedResearchers);
                setAvailableRoles([...rolesResponse.result.defaultRoles, ...rolesResponse.result.customRoles]);
            } catch (err) {
                console.log("Error fetching data:", err);
            }
            setIsLoadingShare(false);
        }
        if(props.investigations.data && props.investigations.currentInvestigation){
            fetchData(props.investigations.currentInvestigation.uuid);
        }
    }, [props.investigations])
    
    useEffect(() => {
        if (searchText === "") {
            setFilteredResearchers(sharedResearchers);
        } else {
            const filtered = sharedResearchers.filter((researcher) => {
                const name = researcher.name ? 
                    `${researcher.name} ${researcher.surnames}`.toLowerCase() : 
                    researcher.email.toLowerCase();
                return name.includes(searchText.toLowerCase());
            });
            setFilteredResearchers(filtered);
        }
    }, [searchText, sharedResearchers])
    
    function renderNewResearchers(){
        if(newResearchers.length === 0){
            return null;
        }
        else{
            return ([
                <Modal key="modal"
                    open={showModal}
                    closeModal={resetModal}
                    confirmAction={sendInvitations}
                    title={props.translate("investigation.share.confirm_dialog.title")}>
                        <Typography variant="body2" gutterBottom>
                            <Translate id="investigation.share.confirm_dialog.description" />
                        </Typography>
                    </Modal>,
                    <Grid container spacing={3} padding={3}>
                        <Grid item xs={12}>
                            <EnhancedTable titleTable={<Translate id="investigation.share.new_researchers" />}  noSelectable
                                    headCells={Object.keys(RESEARCHER_FORM).map(key => {
                                        return { id: key, alignment: "left", label: <Translate id={RESEARCHER_FORM[key].label} />}
                                    })}
                                    rows={newResearchers.map(researcher => {
                                        let tempSection = {}
                                        for(const keyField of Object.keys(RESEARCHER_FORM)){
                                            const field = RESEARCHER_FORM[keyField];
                                            if(field.type === "select"){
                                                const role = availableRoles.find(r => r.id.toString() === researcher.roleId);
                                                const roleName = role ? role.name : "";
                                                const translatedName = props.translate("investigation.share.roles." + roleName);
                                                tempSection[keyField] = <ColourChip rgbcolor={blue[500]} label={translatedName.indexOf("Missing") !== -1 ? roleName : translatedName} />
                                            }
                                            else{
                                                tempSection[keyField] = researcher[keyField];
                                            }
                                        }
                                        return tempSection;
                                    })}
                                    // actions={[{"type" : "delete", "func" : (index) => removeResearcher(index)}]}
                                />
                        </Grid>
                        <Grid item xs={12} >
                            <ButtonContinue onClick={shareInvestigation} data-testid="submit" color="green" spaceright={1} >
                                <Translate id="investigation.share.share" />
                            </ButtonContinue>
                        </Grid>
                    </Grid>
            ]
            )
        }
    }

    function addResearcher(researcher){
        resetModal();
        setNewResearchers(array => [...array, researcher]);
    }
    function renderPrevResearchers(){
        let content = null;
        if(sharedResearchers.length === 0){
            content = <Box mt={3}>
                            <TypographyStyled variant="body2" component="div" gutterBottom>
                                <Translate id="investigation.share.no_researchers_added" />
                            </TypographyStyled>
                        </Box>
        }
        else{
            const columnsTable = ["name", "status", "role", "app_version"];
            const arrayHeader = columnsTable.map(col => {
                return { id: col, alignment: "left", label: <Translate id={`investigation.share.researcher.${col}`} /> }
            })
    
            content = <><Grid item xs={12}>
                    <SearchBox 
                        hideSearchPatient={false}
                        textField={{
                            label: "investigation.share.search_researcher",
                            helperText: "investigation.share.search_researcher_helper",
                            callBack: setSearchText
                        }}
                        filterItems={[]}
                    />
                </Grid>
                <EnhancedTable noSelectable titleTable={<Translate id="investigation.share.current_researchers" />}  
                        headCells={arrayHeader} order={{"property" : "name", "orderBy" : "desc" }}
                        rows={filteredResearchers.map((researcher, idx) => {
                            const name = researcher.name ? researcher.name+" "+researcher.surnames : researcher.email;

                            let row = {
                                    id:researcher.uuid,
                                    name : name,
                                    role : <PermissionChip role={researcher.role} availableRoles={availableRoles} />,
                                    roleName : researcher.role?.name || "",
                                    status : <StatusChip value={researcher.status}/>,
                                    statusName: researcher.status,
                                    // SKIN-21: SkincAIr build + last-seen, from the API's
                                    // getSharedResearchersStatus (Researcher.clientMeta).
                                    app_version: <ResearcherVersionCell
                                        appVersion={researcher.lastAppVersion}
                                        lastSeenAt={researcher.lastSeenAt}
                                    />,
                                }
                            
                            return row;
                        })}
                        actions={[{"type" : "edit" , "func" : (idx) => editAResearcher(idx)},
                            { "type": "resend",
                              "tooltip": props.translate("investigation.share.resend_invitation"),
                              "check": (row) => row.statusName === 0,
                              "func" : (uuid) => resendInvitation(uuid) },
                            { "type": "reset_password",
                              "tooltip": props.translate("investigation.share.reset_password"),
                              "check": (row) => row.statusName !== 0 && props.investigations.currentInvestigation.permissions.includes(PERMISSION.RESET_PASSWORD),
                              "func" : (uuid) => resetAResearcherPassword(uuid) },
                            { "type": "delete", "check": () => {
                                return props.investigations.currentInvestigation.permissions.includes(PERMISSION.DELETE_RESEARCHER)},
                            "func" : (index) => deleteAResearcher(index) }
                        ]}
        /></>
        }
        return (
            <Grid item  xs={12}>
                {content}
            </Grid>
        )
    }
    function renderModal(){
        let title;
        let modalProps = {};
        if(addingResearcher){
            title = props.translate("investigation.share.add_researcher");
        }
        else if(indexResearcherToEdit){
            title =  props.translate("investigation.share.edit_researcher");
        }
        else if(indexResearcherToDelete !== false){
            title =  props.translate("investigation.share.delete_researcher");
            modalProps = {
                confirmAction: handleDeleteResearcher,
                confirmButtonLabel: "general.delete",
                researcherToDelete: sharedResearchers[indexResearcherToDelete]
            };
        }
        else if(researcherToResend){
            title = props.translate("investigation.share.resend_invitation");
            modalProps = {
                confirmAction: handleResendInvitation,
                confirmButtonLabel: "investigation.share.resend_invitation",
            };
        }
        else if(researcherToReset){
            title = props.translate("investigation.share.reset_password");
            // ResetPasswordFlow drives its own confirmation + temp-password display,
            // so we don't wire Modal's confirmAction.
        }
        else{
            title =  props.translate("investigation.share.info_roles");
           
        }
        
        return(
            <Modal key="modal" open={ showModal } 
                closeModal={resetModal}
                title={title}
                // confirmAction={handleDelete}
                // confirmAction={confirmAction}
                // confirmButtonLabel={confirmButtonLabel}
                {...modalProps}
                
                >
                    {
                        indexResearcherToEdit !== false &&
                        <Form fields={RESEARCHER_FORM} fullWidth callBackForm={editCallBack}
                            initialData={{
                                email: sharedResearchers[indexResearcherToEdit].email,
                                roleId: (sharedResearchers[indexResearcherToEdit].role?.id != null
                                    ? String(sharedResearchers[indexResearcherToEdit].role.id)
                                    : "0")
                            }}
                            closeCallBack={resetModal}/>
                    }
                    {
                        
                        indexResearcherToDelete !== false &&

                       <>
                    <Typography variant="body2" gutterBottom>
                        <Translate id="investigation.share.delete_Confirmation_user" />
                    </Typography>

                </>

                    }
                    {
                        researcherToResend &&
                        <Typography variant="body2" gutterBottom>
                            <Translate id="investigation.share.resend_confirmation" />
                            {" "}<strong>{researcherToResend.email}</strong>
                        </Typography>
                    }
                    {
                        researcherToReset &&
                        <ResetPasswordFlow
                            target={researcherToReset}
                            investigationUuid={props.investigations.currentInvestigation.uuid}
                            onClose={resetModal}
                        />
                    }
                    {
                        addingResearcher &&
                        <Form fields={RESEARCHER_FORM} fullWidth callBackForm={addResearcher} 
                            closeCallBack={resetModal}/>
                    }
                    {
                        showingRoles &&
                        <UserRoles />
                    }
            </Modal>
        )
    }

    async function handleDeleteResearcher() {
//        
            const response = await deleteResearcher(props.investigations.currentInvestigation.uuid,researcherToDelete.uuid);
            console.log("Response from delete user:", response);
            console.log("sharedResearchers",sharedResearchers)
            setSharedResearchers(prev => prev.filter((researcher) => researcher.uuid !== researcherToDelete.uuid));
            //filter by uuid
      
        resetModal();
    }
    // async function hideDeleteIcon(index) {
        
    // }

    async function resetAResearcherPassword(uuid) {
        const found = sharedResearchers.find(r => r.uuid === uuid);
        if (!found) return;
        setResearcherToReset(found);
        setShowModal(true);
    }

    async function deleteAResearcher(uuid) {
        const findIndex = sharedResearchers.findIndex(researcher => researcher.uuid === uuid);


        console.log("Confirm to delete:", sharedResearchers[findIndex]);
        const deleteResearcherUser = sharedResearchers[findIndex];
        setIndexResearcherToDelete(true);//this state shows the delete buttons 
        setResearcherToDelete(deleteResearcherUser);
        setShowModal(true);
        //name of the researcher should be shown in modal
    }

    async function editCallBack(values){
        const assignments = [{
            uuidResearcher: sharedResearchers[indexResearcherToEdit].uuid,
            roleId: parseInt(values["roleId"])
        }];
        setIsLoadingShare(true);
        try {
            const response = await assignRoleBatchService(props.investigations.currentInvestigation.uuid, assignments);
            let copySharedResearchers = [...sharedResearchers];
            if (response.result.assigned.length > 0) {
                const assigned = response.result.assigned[0];
                copySharedResearchers[indexResearcherToEdit] = {
                    ...copySharedResearchers[indexResearcherToEdit],
                    role: assigned.roleId != null && assigned.roleName != null
                        ? { id: assigned.roleId, name: assigned.roleName }
                        : copySharedResearchers[indexResearcherToEdit].role
                };
            }
            setSharedResearchers(copySharedResearchers);
        } catch (error) {
            console.log("Error assigning role:", error);
        }
        setIsLoadingShare(false);
        resetModal();
    }


    function editAResearcher(uuid){
        const findIndex = sharedResearchers.findIndex(researcher => researcher.uuid === uuid);
        setIndexResearcherToEdit(findIndex);
        setShowModal(true);
    }
    function resendInvitation(uuid){
        const found = sharedResearchers.find(r => r.uuid === uuid);
        if(!found) return;
        setResearcherToResend(found);
        setShowModal(true);
    }
    async function handleResendInvitation(){
        if(!researcherToResend) return;
        // SHER-804 — one at a time: a double click would mint two tokens and silently kill the
        // first link.
        if(resendInFlight.current) return;
        const target = researcherToResend;
        resetModal();
        resendInFlight.current = true;
        try {
            // SHER-803/804 — a fresh token, the material re-wrapped under it, and its link to hand
            // over. The old resend only re-sent an email, which carries no link any more.
            const keys = resolveInviterKeys();
            if(!keys) return;
            const result = await resendInvitationWithToken(
                props.investigations.currentInvestigation.uuid,
                target.email,
                keys.rawKeyInvestigation,
                window.location.origin,
                keys.keks
            );
            if(result.status === 200){
                setInvitationLinks([result.link]);
            }
            else{
                setShowSnackbar({show:true, message:"investigation.share.resend_failed", severity:"error"});
            }
        } catch (err) {
            console.log("Error resending invitation:", err);
            setShowSnackbar({show:true, message:"investigation.share.resend_failed", severity:"error"});
        } finally {
            resendInFlight.current = false;
        }
    }
    function showInfo(){
        setShowingRoles(true);
        setShowModal(true);
    }

    if(props.investigations.loading || !investigation || isLoadingShare){
        return <Loader />
    }
    return (
        <BoxBckgr color="text.primary" >
            <Helmet title={props.translate("investigation.share.title")} />
            {
                renderModal()
            }
            {/* SHER-803 — no onClose: a stray click outside must not throw away links that cannot be shown again. */}
            <Dialog open={Boolean(invitationLinks)} maxWidth="sm" fullWidth>
                {invitationLinks && (
                    <InvitationLinks
                        investigationName={investigation.name}
                        links={invitationLinks}
                        onCopy={copyInvitationLink}
                        onEmail={(mailtoUrl) => { window.location.href = mailtoUrl; }}
                        onClose={() => setInvitationLinks(null)}
                    />
                )}
            </Dialog>
            <Snackbar
                anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
                }}
                open={showSnackbar.show}
                autoHideDuration={2000}
                onClose={() => setShowSnackbar({show:false})}>
                    <div>
                        {
                            (showSnackbar.message && showSnackbar.severity) &&
                            <Alert onClose={() => setShowSnackbar({show:false})} severity={showSnackbar.severity}>
                                <Translate id={showSnackbar.message} />
                            </Alert>
                        }
                </div>
            </Snackbar>
            <Grid container spacing={3} padding={3}>
                <SectionHeader section="users" infoCallback={showInfo} />
                <Grid item xs={12}>
                    <TypographyStyled gutterBottom variant="h5" component="h2">
                        { investigation.name }
                    </TypographyStyled>
                </Grid>
                <Grid item xs={12}>
                    <TabsSherwood name="users-tabs"
                        labels={[props.translate("investigation.share.tab.users"), props.translate("investigation.share.tab.roles")]}
                        whiteBackground={false}
                        tabChangeCallback={(tabSelected) => {
                            if (tabSelected === 0 && investigation?.uuid) {
                                refetchRoles(investigation.uuid);
                            }
                        }}>
                        <Grid container spacing={3}>
                            {
                                renderPrevResearchers()
                            }
                            <Grid item xs={12} >
                                <TypographyStyled variant="body2" component="div" gutterBottom>
                                    <Translate id="investigation.share.add_researcher" />
                                    <ButtonAdd disabled={addingResearcher}
                                        type="button" data-testid="add_researcher"
                                        onClick={() => {
                                            setAddingResearcher(true);
                                            setShowModal(true);
                                        }}></ButtonAdd>
                                </TypographyStyled>
                            </Grid>
                            {
                                renderNewResearchers()
                            }
                        </Grid>
                        <RolesManagement uuidInvestigation={investigation.uuid} />
                    </TabsSherwood>
                </Grid>
            </Grid>
        </BoxBckgr>
    )
}



const mapStateToProps = (state) =>{
    return {
        investigations : state.investigations
    }
}

export default withLocalize(connect(mapStateToProps, null)(ShareInvestigation))